import { useContext, useEffect, useMemo, useState } from 'react'
import { ContentLabel, GifLabels } from 'types/src/content-label'
import Button from 'ui/src/components/controls/button'
import DropdownButton from 'ui/src/components/controls/dropdown-button'
import Pill from 'ui/src/components/controls/pill'
import Collapsible from '../../../controls/collapsible'
import { CONTROL_SIZE } from '../../constants'
import { EditPanelApiContext } from '../../contexts/api'
import { EditPanelContext } from '../../contexts/panel'
import { Input, LabelSelectList, Message, Pills } from './style'

type LabelCount = ContentLabel & { count: number }

const EditPanelInfoLabels = () => {
    const { addGifLabel, fetchGifLabels, removeGifLabel } = useContext(EditPanelApiContext)
    const { colors, gifIds, permissions } = useContext(EditPanelContext)
    const [data, setData] = useState<GifLabels>()
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [selectedLabelId, setSelectedLabelId] = useState('')
    const gifIdsKey = gifIds.join(',')

    useEffect(() => {
        if (!permissions.labels || gifIds.length === 0) return
        let active = true
        setData(undefined)
        setError('')
        fetchGifLabels(gifIds)
            .then((labels) => active && setData(labels))
            .catch(() => active && setError('Unable to load content labels.'))
        return () => {
            active = false
        }
        // gifIdsKey is the stable dependency for the array supplied by the panel context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchGifLabels, gifIdsKey, permissions.labels])

    const attached = useMemo<LabelCount[]>(() => {
        const counts = new Map<number, LabelCount>()
        Object.values(data?.gif_labels || {}).forEach((labels) => {
            labels.forEach((label) => {
                const current = counts.get(label.id)
                counts.set(label.id, { ...label, count: (current?.count || 0) + 1 })
            })
        })
        return Array.from(counts.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [data])

    const selectableLabels = useMemo(
        () =>
            data?.available_labels.filter((label) =>
                gifIds.some((gifId) => !(data.gif_labels[gifId] || []).some(({ id }) => id === label.id))
            ) || [],
        [data, gifIds]
    )

    if (!permissions.labels) return null

    const updateLocalLabels = (label: ContentLabel, targetGifIds: string[], add: boolean) => {
        setData((current) => {
            if (!current) return current
            const gifLabels = { ...current.gif_labels }
            targetGifIds.forEach((gifId) => {
                const labels = gifLabels[gifId] || []
                gifLabels[gifId] = add
                    ? labels.some(({ id }) => id === label.id)
                        ? labels
                        : [...labels, label]
                    : labels.filter(({ id }) => id !== label.id)
            })
            return { ...current, gif_labels: gifLabels }
        })
    }

    const onAdd = async () => {
        if (!data || !selectedLabelId || saving) return
        const label = data.available_labels.find(({ id }) => id === Number(selectedLabelId))
        if (!label) return
        const targets = gifIds.filter((gifId) => !(data.gif_labels[gifId] || []).some(({ id }) => id === label.id))
        if (targets.length === 0) return

        setSaving(true)
        setError('')
        try {
            await addGifLabel(targets, label.id)
            updateLocalLabels(label, targets, true)
            setSelectedLabelId('')
        } catch {
            setError('Unable to add the content label.')
        } finally {
            setSaving(false)
        }
    }

    const onRemove = async (label: ContentLabel) => {
        if (!data || saving) return
        const targets = gifIds.filter((gifId) => (data.gif_labels[gifId] || []).some(({ id }) => id === label.id))
        setSaving(true)
        setError('')
        try {
            await removeGifLabel(targets, label.id)
            updateLocalLabels(label, targets, false)
        } catch {
            setError('Unable to remove the content label.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Collapsible
            arrowColor={colors.labelArrow || colors.labelIcon}
            color={colors.label}
            defaultOpen
            icon="tag"
            iconColor={colors.labelIcon}
            title="Content Labels"
        >
            <Input>
                <DropdownButton
                    color={colors.input}
                    disabled={!data || saving}
                    label={
                        selectableLabels.find(({ id }) => id === Number(selectedLabelId))?.name || 'Add Content Label'
                    }
                    size={CONTROL_SIZE}
                    style={{ flex: 1 }}
                    textColor={colors.backgroundColor}
                >
                    <LabelSelectList
                        activeColor={colors.button}
                        iconColor={colors.buttonText}
                        index={selectableLabels.findIndex(({ id }) => id === Number(selectedLabelId))}
                        options={selectableLabels.map(({ name }) => name)}
                        textColor={colors.buttonText}
                        onChange={(index) => setSelectedLabelId(String(selectableLabels[index].id))}
                    />
                </DropdownButton>
                <Button
                    color={colors.button}
                    disabled={!selectedLabelId || saving || selectableLabels.length === 0}
                    loading={saving}
                    size={CONTROL_SIZE}
                    textColor={colors.buttonText}
                    aria-label="Add content label"
                    onClick={onAdd}
                >
                    Add
                </Button>
            </Input>
            <Pills>
                {attached.map((label) => (
                    <Pill
                        color={label.count === gifIds.length ? colors.sharedTag : colors.control}
                        key={label.id}
                        removeable={!saving}
                        removeOptions={{ title: `Remove ${label.name}` }}
                        round
                        size={CONTROL_SIZE}
                        onRemove={() => onRemove(label)}
                    >
                        {label.name}
                        {gifIds.length > 1 ? ` (${label.count}/${gifIds.length})` : ''}
                    </Pill>
                ))}
            </Pills>
            {!data && !error && <Message>Loading content labels…</Message>}
            {data && attached.length === 0 && <Message>No content labels attached.</Message>}
            {error && <Message $error>{error}</Message>}
        </Collapsible>
    )
}

export default EditPanelInfoLabels
